import { useMemo, useState } from "react";
import { ChevronDown, ArrowUpDown } from "lucide-react";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { short } from "@/lib/creator360";
import { agoWords, comma, day, daysAgo, join } from "@/lib/c360Profile";
import { bl, useBlur } from "@/lib/c360Mine";

const PER = [["all", "All", null], ["1y", "1Y", 365], ["6m", "6M", 182], ["3m", "3M", 91], ["1m", "1M", 30], ["7d", "7D", 7]];
const TYPE = (t) => (t === "live" ? "Live" : t === "short" || t === "shorts" ? "Short" : "Standard");
const MIX_COLOR = { Standard: "#00F0FF", Live: "#F43F5E", Short: "#FBBF24" };
const thumb = (v) => (v.vid ? `https://i.ytimg.com/vi/${v.vid}/mqdefault.jpg` : null);

function bucketOf(iso, cad) {
  const dt = new Date(iso);
  if (cad === "monthly") return iso.slice(0, 7);
  if (cad === "weekly") { dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7)); return dt.toISOString().slice(0, 10); }
  return iso.slice(0, 10);
}

function series(vids, cad) {
  const m = new Map();
  for (const v of vids) { const k = bucketOf(v.date, cad); m.set(k, (m.get(k) || 0) + 1); }
  return [...m.entries()].sort().map(([k, n]) => ({ k, n, label: cad === "monthly" ? k : day(k)?.replace(/ \d{4}$/, "") }));
}

function exTops(vids, today) {
  const m = new Map();
  for (const v of vids) for (const e of v.ex) { const x = m.get(e) || { n: 0, last: "" }; x.n += 1; if (v.date > x.last) x.last = v.date; m.set(e, x); }
  return [...m.entries()].sort((a, b) => b[1].n - a[1].n).slice(0, 4).map(([name, x]) => ({ name, n: x.n, last: agoWords(daysAgo(x.last, today)) }));
}

export function PromoTimeline({ d }) {
  const [per, setPer] = useState("all");
  const all = d.videos.filter((v) => v.date);
  const days = PER.find((p) => p[0] === per)[2];
  const cut = days ? new Date(new Date(d.today) - days * 864e5).toISOString().slice(0, 10) : "";
  const vids = all.filter((v) => v.date >= cut);
  const cad = per === "all" || per === "1y" ? "monthly" : per === "7d" || per === "1m" ? "daily" : "weekly";
  const data = useMemo(() => series(vids, cad), [vids, cad]);
  const mix = ["Standard", "Live", "Short"].map((k) => ({ name: k, value: vids.filter((v) => TYPE(v.type) === k).length })).filter((x) => x.value);
  const tops = exTops(vids, d.today);
  const first = all.length ? all[all.length - 1].date : null;
  if (!all.length) return <div className="rounded-lg border hairline p-5 text-sm text-zinc-400" data-testid="c360-panel-yt-promo">{d.creator.seg === "unaffiliated" ? "They promote no exchange, so there is no promo timeline." : "No promo videos on record for them yet."}</div>;
  return (
    <div className="rounded-lg border hairline p-5 c360-rise" style={{ background: "var(--surface)" }} data-testid="c360-panel-yt-promo">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base md:text-lg text-white font-medium" data-testid="c360-promo-title">{per === "all" ? `${comma(all.length)} promo videos since their first one` : `${comma(vids.length)} promo videos in this period`}</h3>
        <div className="flex gap-1">{PER.map(([k, l]) => <button key={k} type="button" onClick={() => setPer(k)} data-testid={`c360-promo-per-${k}`} className={`mono text-[11px] px-2.5 py-1 rounded border ${per === k ? "border-cyan-400 text-cyan-300" : "hairline text-zinc-400"}`}>{l}</button>)}</div>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 mt-4">
        <div className="flex-1 h-56 min-w-0">
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="label" stroke="#52525B" fontSize={10} tickLine={false} />
              <YAxis stroke="#52525B" fontSize={10} allowDecimals={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} formatter={(v) => [v, "promo videos"]} />
              <Bar dataKey="n" fill="#00F0FF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:w-64 space-y-3 text-sm">
          <div><div className="text-[11px] text-zinc-500">Promo videos on record</div><div className="text-cyan-300 font-semibold">{comma(all.length)}</div></div>
          <div><div className="text-[11px] text-zinc-500">First promo video</div><div className="text-zinc-200">{day(first)}</div></div>
          <div><div className="text-[11px] text-zinc-500">Last upload</div><div className="text-zinc-200">{agoWords(d.creator.days) || "Not known"}</div></div>
          {tops.length > 0 && <div data-testid="c360-promo-ex"><div className="text-[11px] text-zinc-500">Top exchanges</div>{tops.map((t) => <div key={t.name} className="text-zinc-200 text-xs">{t.name} · {t.n} · last {t.last}</div>)}</div>}
        </div>
        <div className="lg:w-44 flex flex-col items-center" data-testid="c360-promo-mix">
          <div className="w-32 h-32"><ResponsiveContainer><PieChart><Pie data={mix} dataKey="value" innerRadius={38} outerRadius={58} stroke="none">{mix.map((m) => <Cell key={m.name} fill={MIX_COLOR[m.name]} />)}</Pie></PieChart></ResponsiveContainer></div>
          <div className="space-y-0.5 mt-1">{mix.map((m) => <div key={m.name} className="mono text-[10px] text-zinc-400"><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: MIX_COLOR[m.name] }} />{m.name === "Short" ? "Shorts" : m.name} {Math.round((m.value * 100) / vids.length)}%</div>)}</div>
        </div>
      </div>
      {tops.length > 0 && <p className="text-xs text-zinc-500 mt-4" data-testid="c360-promo-note">Their promo videos are mostly about {join(tops.slice(0, 3).map((t) => t.name))}.</p>}
    </div>
  );
}

export function VideoCard({ v, i, extra, testid }) {
  const blur = useBlur();
  return (
    <a href={blur ? undefined : v.url} target="_blank" rel="noreferrer" className="block rounded-lg border hairline overflow-hidden hover:border-white/25 transition-colors" style={{ background: "var(--surface)" }} data-testid={testid}>
      {thumb(v) && <div className="overflow-hidden"><img src={thumb(v)} alt="" className={`w-full aspect-video object-cover ${bl(blur, "media")}`} loading="lazy" /></div>}
      <div className="p-3">
        <div className="mono text-[10px] text-cyan-400">#{i + 1} · {short(v.views)} views · {short(v.likes)} likes</div>
        <div className={`text-sm text-zinc-200 line-clamp-2 mt-1 ${bl(blur)}`} data-testid={`${testid}-title`}>{v.title}</div>
        <div className="mono text-[10px] text-zinc-500 mt-1">{day(v.date)}{v.ex?.length ? ` · ${v.ex.join(", ")}` : ""}</div>
        {extra}
      </div>
    </a>
  );
}

export function TopPromo({ d }) {
  const top = [...d.videos].sort((a, b) => b.views - a.views).slice(0, 3);
  if (!top.length) return null;
  return (
    <section data-testid="c360-top-promo">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Top 3 promo videos by views</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{top.map((v, i) => <VideoCard key={v.url} v={v} i={i} testid={`c360-top-promo-${i}`} />)}</div>
    </section>
  );
}

const th = "px-3 py-2.5 text-left font-normal mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 whitespace-nowrap";
const COLS = [["date", "Date"], ["type", "Type"], ["title", "Video"], ["views", "Views"], ["likes", "Likes"], ["comments", "Comments"], ["eng", "Eng."], ["ex", "Exchange"]];
const eng = (v) => (v.views && v.likes > 0 ? ((v.likes + v.comments) * 100) / v.views : null);

export function PromoHistory({ d }) {
  const blur = useBlur();
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState(["date", -1]);
  const [n, setN] = useState(25);
  const rows = useMemo(() => {
    const val = (v) => (sort[0] === "eng" ? eng(v) ?? -1 : sort[0] === "ex" ? v.ex.join(",") : v[sort[0]]);
    return [...d.videos].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort[1]);
  }, [d.videos, sort]);
  if (!d.videos.length) return null;
  return (
    <section className="border hairline rounded-lg overflow-hidden" style={{ background: "var(--surface)" }} data-testid="c360-promo-history">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="c360-promo-history-toggle" aria-expanded={open}>
        <span className="text-sm text-zinc-200">View individual video history <span className="text-zinc-500">({comma(d.videos.length)} promo videos on record)</span></span>
        <ChevronDown size={15} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="overflow-x-auto border-t hairline">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr>{COLS.map(([k, l]) => <th key={k} className={th}><button type="button" onClick={() => setSort([k, sort[0] === k ? -sort[1] : -1])} className="inline-flex items-center gap-1" data-testid={`c360-hist-sort-${k}`}>{l}<ArrowUpDown size={10} /></button></th>)}</tr></thead>
            <tbody>{rows.slice(0, n).map((v) => (
              <tr key={v.url} className="border-t hairline" data-testid="c360-hist-row">
                <td className="px-3 py-2 mono text-xs text-zinc-400 whitespace-nowrap">{day(v.date)}</td>
                <td className="px-3 py-2 mono text-[10px] text-zinc-400">{TYPE(v.type)}</td>
                <td className="px-3 py-2 max-w-[320px]"><a href={v.url} target="_blank" rel="noreferrer" className={`line-clamp-1 text-zinc-200 hover:text-cyan-300 ${bl(blur, "link")}`}>{v.title}</a></td>
                <td className="px-3 py-2 mono text-xs">{short(v.views)}</td>
                <td className="px-3 py-2 mono text-xs">{v.likes > 0 ? comma(v.likes) : "Hidden"}</td>
                <td className="px-3 py-2 mono text-xs">{comma(v.comments)}</td>
                <td className="px-3 py-2 mono text-xs">{eng(v) == null ? "—" : `${eng(v).toFixed(2)}%`}</td>
                <td className="px-3 py-2 mono text-[11px] text-zinc-400">{v.ex.join(", ") || "—"}</td>
              </tr>))}</tbody>
          </table>
          {n < rows.length && <button type="button" onClick={() => setN(n + 50)} className="w-full py-2.5 border-t hairline text-xs text-zinc-300 hover:bg-white/5" data-testid="c360-hist-more">Show more ({rows.length - n} left)</button>}
        </div>
      )}
    </section>
  );
}
